import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import type { SegmentCriteria } from './segments';

// ── Campaign Templates (/api/campaign-templates) ────────────────────────

export interface CampaignTemplate {
  id: string;
  name: string;
  vertical: string;
  icon?: string | null;
  description?: string | null;
  agent_id?: string | null;
  workflow_id?: string | null;
  expected_cpa?: string | null;
  expected_book_rate?: string | null;
  tags: string[];
  is_active: boolean;
  /** Real workflow shape (ticket 20) — "Use Template" applies these onto the
   * new campaign, not just name/vertical/agent/workflow. */
  trigger_type?: 'inbound' | 'lead_triggered_outbound' | 'drip_cadence' | 'segmented_outbound';
  /** BUG-40 — the real 12-value campaign_type (models/campaign_type.py),
   * added alongside trigger_type because that alone couldn't distinguish
   * e.g. a static_qr template from a plain inbound_line one. Prefer this
   * over the trigger_type -> flow guess in campaignTemplateAdapter.ts
   * whenever it's set. */
  campaign_type?: string | null;
  transfer_directory?: { name: string; role?: string | null; phone: string; situations?: string[] }[];
  business_hours?: Record<string, unknown>;
  drip_day_offsets?: number[];
  default_agent_greeting?: string | null;
  default_agent_prompt?: string | null;
  knowledge_base_ids?: string[];
  /** BOOSTT-3179 — applied via applyCampaignTemplate() once the new campaign
   * (and its workspace) exist, not at builder-open time like the fields above. */
  sms_template_body?: string | null;
  segment_criteria?: SegmentCriteria | null;
  created_at: string;
  updated_at: string;
}

export async function fetchCampaignTemplates(params?: { vertical?: string; include_inactive?: boolean }, axiosInstance?: AxiosInstance): Promise<CampaignTemplate[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CampaignTemplate[]>(apiConfig.voiceEndpoints.campaignTemplates, { params });
  return response.data;
}

export async function createCampaignTemplate(data: Partial<CampaignTemplate>, axiosInstance?: AxiosInstance): Promise<CampaignTemplate> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<CampaignTemplate>(apiConfig.voiceEndpoints.campaignTemplates, data);
  return response.data;
}

export async function updateCampaignTemplate(id: string, data: Partial<CampaignTemplate>, axiosInstance?: AxiosInstance): Promise<CampaignTemplate> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.put<CampaignTemplate>(`${apiConfig.voiceEndpoints.campaignTemplates}/${id}`, data);
  return response.data;
}

export async function deleteCampaignTemplate(id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.campaignTemplates}/${id}`);
  return response.status === 200;
}

/** BOOSTT-3179 — call once, right after a campaign is first created from a
 * template: applies sms_template_body/segment_criteria/drip_day_offsets,
 * which live on separate workspace-scoped resources or need the campaign row
 * to already exist, so they can't be set at template-selection time. */
export async function applyCampaignTemplate(
  templateId: string,
  campaignId: string,
  axiosInstance?: AxiosInstance,
): Promise<{ sms_template_created: boolean; saved_segment_created: boolean; default_drip_day_offsets_set: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post(`${apiConfig.voiceEndpoints.campaignTemplates}/${templateId}/apply`, { campaign_id: campaignId });
  return response.data;
}
